import {
  TouchableOpacity,
  Text,
  View,
  Modal,
  TouchableWithoutFeedback,
  ScrollView,
} from "react-native";
import { useState, useEffect } from "react";
import { MapPin, Store, CheckCircle } from "lucide-react-native";
import React from "react";
import {
  SpotOnLocation,
  ConnectionLocation,
} from "../../../types/api/connections";
import { useConnectionsService } from "../../../services/api/connections-service";
import { Checkbox } from "../../../components/common/shadcn/checkbox";
import { useAgents } from "../../../hooks/useAgents";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/common/shadcn/select";
import { Badge } from "../../../components/badge";

interface SpotOnLocationsModalProps {
  visible: boolean;
  onClose: () => void;
}
// pairs a spoton location to a Loman agent
interface SpotOnLocationMapping {
  locationId: string;
  agentId: string | null;
}
const SpotOnLocationsModal = ({
  visible,
  onClose,
}: SpotOnLocationsModalProps) => {
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [availableLocations, setAvailableLocations] = useState<
    SpotOnLocation[]
  >([]);
  const [locationMappings, setLocationMappings] = useState<
    SpotOnLocationMapping[]
  >([]);
  const [isOAuthFlowLoading, setIsOAuthFlowLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    fetchSpotOnConnections,
    fetchSpotOnLocations,
    startSpotOnOAuth,
    updateSpotOnLocations,
  } = useConnectionsService();

  const { data: agents } = useAgents();
  const spotOnAgents = agents?.filter(
    (agent) => agent.posProvider === "spoton"
  );

  useEffect(() => {
    if (visible) {
      loadLocations();
    }
  }, [visible]);

  const loadLocations = async () => {
    try {
      setLocationsLoading(true);
      setLocationsError(null);
      const [locationsData, connections] = await Promise.all([
        fetchSpotOnLocations(),
        fetchSpotOnConnections(),
      ]);

      setAvailableLocations(locationsData || []);

      const spotOnConnections =
        connections?.filter((conn) => conn.provider === "spoton") || [];
      const selectedLocationIds: string[] = [];
      const existingMappings: SpotOnLocationMapping[] = [];

      spotOnConnections.forEach((connection) => {
        if (connection.metadata?.locations) {
          connection.metadata.locations.forEach(
            (location: ConnectionLocation) => {
              selectedLocationIds.push(location.id);
              if (location.agentId) {
                existingMappings.push({
                  locationId: location.id,
                  agentId: location.agentId,
                });
              }
            }
          );
        }
      });

      setSelectedLocations(selectedLocationIds);
      setLocationMappings(existingMappings);
    } catch (error) {
      console.error("Failed to load locations:", error);
      // if token expired, restart OAuth flow
      if (error instanceof Error && error.message.includes("403")) {
        setIsOAuthFlowLoading(true);
        try {
          const redirectUrl = await startSpotOnOAuth();
          window.location.href = redirectUrl;
        } catch (oauthError) {
          console.error("Failed to restart SpotOn OAuth flow:", oauthError);
          setLocationsError("Failed to re-authenticate. Please try again.");
        } finally {
          setIsOAuthFlowLoading(false);
        }
      } else {
        setLocationsError("Failed to load locations. Please try again.");
      }
    } finally {
      setLocationsLoading(false);
    }
  };

  const handleLocationSelection = (locationId: string, checked: boolean) => {
    if (checked) {
      setSelectedLocations((prev) => [...prev, locationId]);
    } else {
      setSelectedLocations((prev) => prev.filter((id) => id !== locationId));
      setLocationMappings((prev) =>
        prev.filter((m) => m.locationId !== locationId)
      );
    }
  };

  const handleLocationMatch = (locationId: string, agentId: string) => {
    setLocationMappings((prev) => {
      const existing = prev.find((match) => match.locationId === locationId);
      const newMatches = existing
        ? prev.map((match) =>
            match.locationId === locationId ? { ...match, agentId } : match
          )
        : [...prev, { locationId, agentId }];

      setSubmitError(null);

      return newMatches;
    });
  };

  const validateAgentMappings = () => {
    const mappedAgentIds = locationMappings
      .filter((match) => match.agentId)
      .map((match) => match.agentId!);
    const uniqueAgentIds = new Set(mappedAgentIds);
    if (mappedAgentIds.length !== uniqueAgentIds.size) {
      const agentCounts = mappedAgentIds.reduce(
        (acc, agentId) => {
          acc[agentId] = (acc[agentId] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      );
      const duplicateAgentId = Object.keys(agentCounts).find(
        (agentId) => agentCounts[agentId] > 1
      );
      const agentName =
        spotOnAgents?.find((agent) => agent.id === duplicateAgentId)?.name ||
        duplicateAgentId;
      return `Agent "${agentName}" is mapped to multiple locations. Each location must be mapped to a unique agent.`;
    }
    return null;
  };

  const completeSetup = async () => {
    const allMatched = selectedLocations.every((locationId) =>
      locationMappings.some(
        (match) => match.locationId === locationId && match.agentId
      )
    );
    if (!allMatched) {
      setSubmitError("Please match all locations with an agent.");
      return;
    }
    const duplicateError = validateAgentMappings();
    if (duplicateError) {
      setSubmitError(duplicateError);
      return;
    }
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      const selectedLocationObjects = availableLocations
        .filter((location) => selectedLocations.includes(location.id))
        .map((location) => ({
          id: location.id,
          name: location.name,
          address: location.address,
          agentId:
            locationMappings.find((m) => m.locationId === location.id)
              ?.agentId || "",
        }));
      await updateSpotOnLocations({ locations: selectedLocationObjects });
      onClose();
    } catch (error) {
      console.error("Failed to update SpotOn locations:", error);
      setSubmitError("Failed to save location selection. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLocationMatched = (locationId: string) => {
    return locationMappings.some(
      (match) => match.locationId === locationId && match.agentId
    );
  };

  const handleClose = () => {
    setSubmitError(null);
    onClose();
  };

  if (locationsLoading || isOAuthFlowLoading) {
    return (
      <Modal
        visible={visible}
        transparent={true}
        animationType="none"
        onRequestClose={handleClose}
      >
        <TouchableWithoutFeedback onPress={handleClose}>
          <View className="flex-1 bg-black/50 justify-center items-center">
            <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
              <View className="bg-white rounded-xl p-6 w-4/5 max-w-[500px]">
                <View className="flex-row items-center justify-center">
                  <View className="h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <Text className="text-gray-600 ml-3">
                    Loading locations...
                  </Text>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    );
  }

  if (locationsError) {
    return (
      <Modal
        visible={visible}
        transparent={true}
        animationType="none"
        onRequestClose={handleClose}
      >
        <TouchableWithoutFeedback onPress={handleClose}>
          <View className="flex-1 bg-black/50 justify-center items-center">
            <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
              <View className="bg-white rounded-xl p-6 w-4/5 max-w-[500px]">
                <Text className="text-red-600 text-center">
                  {locationsError}
                </Text>
                <TouchableOpacity
                  onPress={handleClose}
                  className="mt-4 bg-gray-100 px-4 py-2 rounded-md"
                >
                  <Text className="text-gray-700 text-center">Close</Text>
                </TouchableOpacity>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    );
  }

  if (
    (!availableLocations || availableLocations.length === 0) &&
    !isOAuthFlowLoading &&
    !locationsLoading
  ) {
    return (
      <Modal
        visible={visible}
        transparent={true}
        animationType="none"
        onRequestClose={handleClose}
      >
        <TouchableWithoutFeedback onPress={handleClose}>
          <View className="flex-1 bg-black/50 justify-center items-center">
            <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
              <View className="bg-white rounded-xl p-6 w-4/5 max-w-[500px]">
                <Text className="text-gray-600 text-center">
                  No SpotOn locations available. Please check your SpotOn
                  connection.
                </Text>
                <TouchableOpacity
                  onPress={handleClose}
                  className="mt-4 bg-gray-100 px-4 py-2 rounded-md"
                >
                  <Text className="text-gray-700 text-center">Close</Text>
                </TouchableOpacity>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="none"
      onRequestClose={handleClose}
    >
      <TouchableWithoutFeedback onPress={handleClose}>
        <View className="flex-1 bg-black/50 justify-center items-center">
          <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
            <View className="bg-white rounded-xl w-4/5 max-w-[600px] h-[80%] flex flex-col">
              <View className="p-6 pb-0">
                <View className="flex-row items-center justify-between mb-0.5">
                  <View className="flex-row items-center">
                    <Store size={20} color="#374151" className="mr-2" />
                    <Text className="text-lg font-semibold text-gray-900">
                      Select SpotOn Locations
                    </Text>
                  </View>
                </View>
                <Text className="text-sm text-gray-600">
                  Select which SpotOn locations you would like to use
                </Text>
              </View>
              <ScrollView
                className="flex-1 px-6"
                contentContainerStyle={{ paddingVertical: 24, gap: 24 }}
              >
                {availableLocations.map((location) => {
                  const isSelected = selectedLocations.includes(location.id);
                  const isMatched = isLocationMatched(location.id);
                  return (
                    <View
                      key={location.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <View className="flex-row items-center">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked: boolean) =>
                            handleLocationSelection(location.id, checked)
                          }
                          disabled={location.selectable === false}
                          className="mr-3"
                        />
                        <View className="flex-1">
                          <TouchableOpacity
                            className="!w-fit"
                            onPress={() =>
                              location.selectable !== false &&
                              handleLocationSelection(location.id, !isSelected)
                            }
                            activeOpacity={
                              location.selectable !== false ? 0.7 : 1
                            }
                            disabled={location.selectable === false}
                          >
                            <Text className="font-medium text-gray-900">
                              {location.name}
                            </Text>
                            <View className="flex-row items-center mt-1">
                              <MapPin
                                size={12}
                                color="#6b7280"
                                className="mr-1"
                              />
                              <Text className="text-sm text-gray-600">
                                {location.address}
                              </Text>
                            </View>
                          </TouchableOpacity>
                        </View>
                        {isSelected && isMatched && (
                          <Badge
                            variant="secondary"
                            className="bg-green-100 text-green-700"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Matched
                          </Badge>
                        )}
                      </View>

                      {isSelected && (
                        <div className="ml-6 mt-3 space-y-2">
                          <Select
                            value={
                              locationMappings.find(
                                (m) => m.locationId === location.id
                              )?.agentId || ""
                            }
                            onValueChange={(agentId) =>
                              handleLocationMatch(location.id, agentId)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select agent..." />
                            </SelectTrigger>
                            <SelectContent>
                              {spotOnAgents?.map((agent, index) => (
                                <SelectItem key={agent.id} value={agent.id}>
                                  <div className="flex flex-col w-full py-1">
                                    <span className="font-medium text-start">
                                      {agent.name}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {agent.address}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </View>
                  );
                })}
              </ScrollView>
              <View className="px-6 pb-6 pt-4 border-t border-gray-200">
                <View className="flex-row items-center justify-between">
                  <View>
                    <Text className="text-sm text-gray-600">
                      {selectedLocations.length} location
                      {selectedLocations.length !== 1 ? "s" : ""} selected
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={completeSetup}
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-md ${
                      !isSubmitting
                        ? "bg-blue-600 active:bg-blue-700"
                        : "bg-gray-300"
                    }`}
                    activeOpacity={!isSubmitting ? 0.8 : 1}
                  >
                    <View className="flex-row items-center">
                      {isSubmitting && (
                        <View className="h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin mr-2" />
                      )}
                      <Text
                        className={`font-medium ${
                          !isSubmitting ? "text-white" : "text-gray-500"
                        }`}
                      >
                        {isSubmitting ? "Saving..." : "Complete Setup"}
                      </Text>
                    </View>
                  </TouchableOpacity>
                </View>
                {submitError && (
                  <View className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <Text className="text-red-600 text-sm text-center">
                      {submitError}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default SpotOnLocationsModal;
